import React, { useState } from 'react';
import { Box, Button, Chip, Collapse, Paper, Stack, Typography } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LearningSettings } from '../../services/learningApi';

const OPEN_KEY = 'learning.knowledge.lifecycleOpen';

type Who = 'ai' | 'auto' | 'you';

const WHO_CHIP: Record<Who, { label: string; color: 'secondary' | 'default' | 'primary' }> = {
  ai: { label: 'AI · uses credits', color: 'secondary' },
  auto: { label: 'Automatic · no AI', color: 'default' },
  you: { label: 'Automatic or you', color: 'primary' },
};

interface Step {
  title: string;
  who: Who;
  when: string;
  body: React.ReactNode;
}

interface Props {
  settings: LearningSettings;
  categoryName: string | null;
}

export const KnowledgeLifecycle: React.FC<Props> = ({ settings, categoryName }) => {
  const [open, setOpenState] = useState(() => localStorage.getItem(OPEN_KEY) !== '0');
  const setOpen = (v: boolean) => {
    setOpenState(v);
    localStorage.setItem(OPEN_KEY, v ? '1' : '0');
  };
  const k = settings.knowledge;
  const staleDays = settings.sampling.window_days * 3;
  const cutoff = Math.round((k.min_similarity ?? 0.35) * 100);

  const steps: Step[] = [
    {
      title: 'Analyze runs',
      who: 'ai',
      when: 'Each analysis (on your schedule, or “Run analysis”)',
      body: (
        <>
          The analyzer model reads a sample of new runs and notes what it sees: “for this input, this result worked / didn’t”, or a general
          fact. It does not write the knowledge document.
        </>
      ),
    },
    {
      title: 'Merge and count',
      who: 'auto',
      when: 'Right after the analysis',
      body: (
        <>
          The same input seen in several runs becomes one entry, and each run adds evidence. New entries start as <b>Waiting</b>.
        </>
      ),
    },
    {
      title: 'Go live',
      who: 'you',
      when: k.auto_publish ? 'During the merge, or whenever you approve' : 'Only when you approve',
      body: k.auto_publish ? (
        <>
          An entry goes <b>In use</b> by itself once {k.min_evidence}+ runs back it with {Math.round(k.min_confidence * 100)}%+ evidence
          strength. You can approve, pin or reject at any time.
        </>
      ) : (
        <>Auto-approve is off, so entries stay Waiting until you approve them. You can also pin or reject.</>
      ),
    },
    {
      title: 'Publish',
      who: 'auto',
      when: 'After every analysis, and seconds after any change you make',
      body: (
        <>
          Rebuilds {categoryName ? <b>{categoryName}</b> : 'the learned knowledge category'} from the In use entries: one searchable
          snippet (embedding) per entry, plus a readable document. Waiting, rejected and stale entries are left out.
        </>
      ),
    },
    {
      title: 'Used by runs',
      who: 'auto',
      when: 'Every run',
      body: k.inject ? (
        <>
          Each run gets up to {k.top_k} snippets: entries named in its input first, then ones at least {cutoff}% similar. Unrelated
          inputs get nothing.
        </>
      ) : (
        <>Off: “Use the knowledge automatically” is turned off in Settings, so runs don’t get it yet.</>
      ),
    },
  ];

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Stack direction="row" alignItems="center">
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          How learned knowledge works
        </Typography>
        <Button size="small" onClick={() => setOpen(!open)} endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}>
          {open ? 'Hide' : 'Show'}
        </Button>
      </Stack>
      <Collapse in={open}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(5, 1fr)' }, gap: 1.5, mt: 1.5 }}>
          {steps.map((s, i) => (
            <Box key={s.title} sx={{ p: 1.5, borderRadius: 1, bgcolor: 'action.hover', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Typography variant="body2" fontWeight={600}>
                {i + 1}. {s.title}
              </Typography>
              <Chip size="small" variant="outlined" color={WHO_CHIP[s.who].color} label={WHO_CHIP[s.who].label} sx={{ alignSelf: 'flex-start' }} />
              <Typography variant="caption" color="text.secondary">
                {s.when}
              </Typography>
              <Typography variant="body2">{s.body}</Typography>
            </Box>
          ))}
        </Box>

        <Typography variant="body2" fontWeight={600} sx={{ mt: 2, mb: 0.5 }}>
          Keeping it up to date
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
          <Typography component="li" variant="body2">
            <b>Day to day there’s nothing to do.</b> Each analysis adds evidence to existing entries and refreshes their items, creates new
            Waiting entries, and republishes.
          </Typography>
          <Typography component="li" variant="body2">
            <b>Old knowledge retires itself.</b> While analyses keep running, entries not seen in runs for {staleDays} days (3× the look-back)
            become Stale and stop being used. Pinned entries never do. A stale entry comes back{k.auto_publish ? ' by itself' : ' when you approve it'}{' '}
            if it shows up again.
          </Typography>
          <Typography component="li" variant="body2">
            <b>Wrong facts get caught.</b> If more runs contradict a live fact than confirm it, it goes back to Waiting for you to decide.
          </Typography>
          <Typography component="li" variant="body2">
            <b>To fix something</b>, edit or reject the entry; it republishes within seconds. Editing stops the analyzer from rewriting that
            entry’s name and text, and rejecting keeps it from being learned again. Deleting doesn’t: the analyzer can relearn it.
          </Typography>
          <Typography component="li" variant="body2">
            <b>To refresh now</b>, click “Run analysis” at the top of the page. It only reads runs it hasn’t analyzed yet (and older ones that
            have since got explicit feedback like a thumbs up or down, if that’s on in Settings), so it’s most useful once there are new runs. <b>Deleting</b> entries
            doesn’t re-analyze old runs; they’re relearned only from new runs as they come in.
          </Typography>
          <Typography component="li" variant="body2">
            <b>“Rebuild snippets”</b> only rebuilds the knowledge category from the entries as they are now. It doesn’t analyze anything or change any
            entry. You rarely need it: use it after a platform update or if the snippet count looks wrong.
          </Typography>
        </Box>
      </Collapse>
    </Paper>
  );
};
